/**
 * RemoteCommandPanel — OCPP CSMS command console for one device.
 *
 * Mounted inside DeviceDetail (under DeviceLogsPanel). Fetches the action
 * catalog from the server, renders a typed form per action, sends through
 * /api/maintenance/remote-commands/{deviceId}, and renders the response.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Zap, Check, AlertTriangle, X, RotateCcw } from "lucide-react";
import {
  maintenanceApi,
  RemoteCommandCatalogEntry,
  RemoteCommandResponse,
} from "../../../../api/maintenance";
import ActionPicker from "./ActionPicker";
import GenericForm from "./GenericForm";
import SetChargingProfileForm from "./forms/SetChargingProfileForm";
import SendLocalListForm from "./forms/SendLocalListForm";
import ResponseCard from "./responses/ResponseCard";
import "./remote-commands.scss";

interface RemoteCommandPanelProps {
  deviceId: number;
  uniqueName: string | null;
}

interface HistoryEntry {
  id: number;
  at: number;
  action: string;
  meta: Record<string, unknown>;
  result?: RemoteCommandResponse;
  error?: string;
  durationMs: number;
}

const MAX_HISTORY = 20;

const RemoteCommandPanel: React.FC<RemoteCommandPanelProps> = ({ deviceId, uniqueName }) => {
  const [catalog, setCatalog] = useState<RemoteCommandCatalogEntry[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [formDefaults, setFormDefaults] = useState<Record<string, unknown> | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<RemoteCommandResponse | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ loc: (string | number)[]; msg: string }[] | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const nextIdRef = useRef(0);

  // ---- catalog ----------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    maintenanceApi
      .getRemoteCommandCatalog()
      .then((res) => {
        if (!cancelled) setCatalog(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setCatalogError(err?.response?.data?.detail || err?.message || "Failed to load catalog");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset state when the user switches actions
  useEffect(() => {
    setLastResult(null);
    setLastError(null);
    setValidationErrors(null);
  }, [selectedAction]);

  const selectedEntry = useMemo(
    () => catalog.find((e) => e.action === selectedAction) || null,
    [catalog, selectedAction],
  );

  // ---- submit -----------------------------------------------------------
  const handleSubmit = useCallback(
    async (meta: Record<string, unknown>) => {
      if (!selectedEntry) return;
      setSubmitting(true);
      setLastError(null);
      setValidationErrors(null);
      const startedAt = performance.now();
      try {
        const res = await maintenanceApi.sendRemoteCommand(deviceId, {
          action: selectedEntry.action,
          meta,
        });
        const dur = performance.now() - startedAt;
        setLastResult(res.data);
        setHistory((prev) => [
          {
            id: nextIdRef.current++,
            at: Date.now(),
            action: selectedEntry.action,
            meta,
            result: res.data,
            durationMs: dur,
          },
          ...prev,
        ].slice(0, MAX_HISTORY));
      } catch (err: unknown) {
        const dur = performance.now() - startedAt;
        const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
        const status = e.response?.status;
        const detail = e.response?.data?.detail;

        if (status === 400 && detail && typeof detail === "object" && "errors" in (detail as Record<string, unknown>)) {
          const errors = (detail as { errors: { loc: (string | number)[]; msg: string }[] }).errors;
          setValidationErrors(errors);
          setLastError("Validation failed");
        } else if (status === 504) {
          setLastError(
            typeof detail === "string"
              ? detail
              : `Device ${uniqueName ?? deviceId} did not respond in time`,
          );
        } else {
          setLastError(
            typeof detail === "string"
              ? detail
              : e.message || "Failed to send command",
          );
        }
        setHistory((prev) => [
          {
            id: nextIdRef.current++,
            at: Date.now(),
            action: selectedEntry.action,
            meta,
            error: typeof detail === "string" ? detail : e.message || "Failed",
            durationMs: dur,
          },
          ...prev,
        ].slice(0, MAX_HISTORY));
      } finally {
        setSubmitting(false);
      }
    },
    [deviceId, uniqueName, selectedEntry],
  );

  // ---- response-card callbacks ------------------------------------------
  const handleEditConfigKey = useCallback((key: string, value: string) => {
    setSelectedAction("changeConfiguration");
    setFormDefaults({ key, value });
    setLastResult(null);
  }, []);

  const handleSeedSendLocalList = useCallback((nextVersion: number) => {
    setSelectedAction("sendLocalList");
    setFormDefaults({ list_version: nextVersion, update_type: "Differential" });
    setLastResult(null);
  }, []);

  const handleReplayHistory = useCallback((entry: HistoryEntry) => {
    setSelectedAction(entry.action);
    setFormDefaults(entry.meta);
    setLastResult(null);
  }, []);

  // ---- form factory -----------------------------------------------------
  const renderForm = () => {
    if (!selectedEntry) return null;

    if (selectedEntry.action === "setChargingProfile") {
      return <SetChargingProfileForm entry={selectedEntry} onSubmit={handleSubmit} submitting={submitting} />;
    }
    if (selectedEntry.action === "sendLocalList") {
      return (
        <SendLocalListForm
          entry={selectedEntry}
          onSubmit={handleSubmit}
          submitting={submitting}
          defaults={formDefaults}
        />
      );
    }
    return (
      <GenericForm
        key={`${selectedEntry.action}-${JSON.stringify(formDefaults ?? {})}`}
        entry={selectedEntry}
        onSubmit={handleSubmit}
        submitting={submitting}
        defaults={formDefaults}
      />
    );
  };

  // ---- render -----------------------------------------------------------
  return (
    <div className="rcmd-panel">
      <div className="rcmd-header">
        <h3 className="rcmd-title">
          <Zap size={16} />
          Remote OCPP Commands
        </h3>
        {uniqueName && (
          <code style={{ fontSize: 11, color: "#888" }}>{uniqueName}</code>
        )}
      </div>

      {catalogError ? (
        <div className="rcmd-error">{catalogError}</div>
      ) : catalog.length === 0 ? (
        <div style={{ padding: 16, fontSize: 13, color: "#888" }}>Loading actions…</div>
      ) : (
        <>
          <div className="rcmd-picker-row">
            <ActionPicker
              catalog={catalog}
              value={selectedAction}
              onChange={(a) => {
                setSelectedAction(a);
                setFormDefaults(undefined);
              }}
              disabled={submitting}
            />
            {selectedAction && (
              <button
                type="button"
                className="maint-btn-small"
                title="Reset form"
                onClick={() => {
                  setSelectedAction(selectedAction);  // trigger re-mount via key
                  setFormDefaults(undefined);
                  setLastResult(null);
                  setLastError(null);
                  setValidationErrors(null);
                }}
              >
                <RotateCcw size={12} /> Reset
              </button>
            )}
          </div>

          {submitting && (
            <div className="rcmd-progress" style={{ marginBottom: 10 }}>
              <span className="rcmd-spinner" /> Sending… (timeout {selectedEntry?.timeout_seconds ?? 30}s)
            </div>
          )}

          {selectedEntry && renderForm()}

          {lastError && (
            <div className="rcmd-error" style={{ marginTop: 8 }}>
              <strong>{lastError}</strong>
            </div>
          )}

          {validationErrors && validationErrors.length > 0 && (
            <div className="rcmd-validation-errors">
              <strong>Fix these fields:</strong>
              <ul>
                {validationErrors.map((v, i) => (
                  <li key={i}>
                    <code>{v.loc.filter((p) => p !== "body" && p !== "meta").join(".")}</code>: {v.msg}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {lastResult && (
            <ResponseCard
              result={lastResult}
              catalogEntry={selectedEntry}
              onEditConfigKey={handleEditConfigKey}
              onSeedSendLocalList={handleSeedSendLocalList}
            />
          )}

          {history.length > 0 && (
            <>
              <div className="rcmd-section-label">History (this session)</div>
              <div className="rcmd-history">
                {history.map((h) => {
                  const isErr = !!h.error || h.result?.status === "device_error";
                  return (
                    <div key={h.id} className="rcmd-history-row" onClick={() => handleReplayHistory(h)}>
                      <span className="rcmd-history-time">
                        {new Date(h.at).toLocaleTimeString()}
                      </span>
                      <span className="rcmd-history-action">{h.action}</span>
                      <span className="rcmd-history-status">
                        {isErr ? (
                          <span style={{ color: "#c62828" }}>✘ {h.error ?? "Failed"}</span>
                        ) : (
                          <span style={{ color: "#2e7d32" }}>
                            ✔ {(h.result?.response as Record<string, unknown> | undefined)?.status as string ?? "OK"}
                          </span>
                        )}
                      </span>
                      <span style={{ color: "#888", fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
                        {Math.round(h.durationMs)}ms
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RemoteCommandPanel;
